import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, Group, useToast } from '@economic/taco';
import { Trans, t } from '@lingui/macro';

import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { PartialInvoiceDistributionType } from '~/types/internal-api';

interface Props {
    cancel: () => void;
    orderId: number;
    distributionType: PartialInvoiceDistributionType;
}

export default function ConvertToInvoice({ cancel, orderId, distributionType }: Props) {
    const navigate = useNavigate();
    const toast = useToast();

    const convertToInvoice = async () => {
        try {
            await internalApiClient.put(`/sales/order/convert-to-invoice/${orderId}`);
            toast.success(t`Success`);
            navigate(`/sales/invoicing/invoices/${orderId}`);
        } catch (error) {
            let errorMessage = t`Something went wrong`;
            if (error.response?.data?.order?.includes('HasDraftPartialInvoices')) {
                errorMessage = t`This order cannot be converted to an invoice, since one or more partial invoices have not been booked.`;
            }
            toast.error(errorMessage);
            cancel();
        }
    };

    return (
        <>
            <Dialog.Title>
                <Trans>Do you want to create the final invoice?</Trans>
            </Dialog.Title>
            <p>
                {distributionType === PartialInvoiceDistributionType.AmountBased ? (
                    <Trans>
                        The amount on this partial invoice matches the remaining amount on the order. If you want to create an
                        invoice on the remaining amount, you can click 'convert'.
                    </Trans>
                ) : (
                    <Trans>
                        The quantity on this partial invoice matches the remaining quantity on the order. If you want to create an
                        invoice on the remaining quantity, you can click 'convert'.
                    </Trans>
                )}
            </p>
            <Dialog.Footer>
                <Group>
                    <Button onClick={cancel}>
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button appearance="primary" onClick={convertToInvoice}>
                        <Trans>Convert</Trans>
                    </Button>
                </Group>
            </Dialog.Footer>
        </>
    );
}
